import { Core } from "@faire/design-tokens";
import { tsv } from "@faire/slate/lib/tsv";
import type { SVGIcon } from "@faire/web--source/slate/icons/SVGIcon";
import React from "react";

import { AvatarIconPosition, AvatarIconSize } from "./types";

export interface IAvatarIconProps {
  /**
   * The icon component to render
   */
  Component: React.FC<SVGIcon.IProps>;

  /**
   * The position of the icon.
   *
   * @default "topRight"
   */
  position?: AvatarIconPosition;

  /**
   * Whether to show the icon container.
   *
   * @default false
   */
  showContainer?: boolean;
}

interface IProps extends IAvatarIconProps {
  /**
   * Icon container className
   */
  className?: string;

  /**
   * The size of the component (including padding and border)
   */
  size: AvatarIconSize;
}

export const AvatarIcon: React.FC<IProps> = ({
  Component: Icon,
  position = "topRight",
  showContainer,
  size,
}) => {
  const containerCls = containerStyle.cls({
    showContainer: !!showContainer,
    position,
    size,
  });

  return (
    <div className={containerCls}>
      <Icon
        style={{ width: "100%", height: "100%" }}
        color={Core.icon.default}
      />
    </div>
  );
};

const containerStyle = tsv({
  base: ["fs-avatar__icon"],
  variants: {
    position: {
      topRight: "bottom-[unset] left-[unset]",
      bottomRight: "top-[unset] left-[unset]",
    } satisfies Record<AvatarIconPosition, string>,
    showContainer: {
      false: ["border-0", "bg-transparent"],
    },
    size: {
      small: "h-4 w-4",
      medium: "h-5 w-5",
      large: "h-6 w-6",
    } satisfies Record<AvatarIconSize, string>,
  },
  compoundVariants: [
    {
      showContainer: true,
      size: "small",
      className: "p-[--spacing(0.75)]",
    },
    {
      showContainer: true,
      size: "medium",
      className: "p-[--spacing(0.75)]",
    },
    {
      showContainer: true,
      size: "large",
      className: "p-[--spacing(1.25)]",
    },
  ],
});
